import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Megaphone, Mail, MessageSquare, Bell, Send, Pause, Play, Trash2, Plus,
    Sparkles, ShoppingCart, Cake, Crown, Heart, Ticket, ArrowLeft, BarChart3,
    Users, Activity, ChevronRight, TrendingUp, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { api } from '@/lib/api';

const ICONS = { Sparkles, ShoppingCart, Cake, Crown, Heart, Ticket, Megaphone };
const CHANNEL_ICONS = { email: Mail, sms: MessageSquare, whatsapp: MessageSquare, push: Bell };
const STATUS_COLORS = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    draft: 'bg-slate-100 text-slate-600 border-slate-200',
    paused: 'bg-amber-100 text-amber-700 border-amber-200',
    archived: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

// ============================================================
// OVERVIEW DASHBOARD
// ============================================================
export default function AdminMarketing() {
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: o }, { data: c }] = await Promise.all([
                api.get('/admin/marketing/overview'),
                api.get('/admin/marketing/campaigns'),
            ]);
            setOverview(o);
            setCampaigns(c);
        } catch {
            toast.error('Could not load marketing data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <AdminLayout>
            <div className="p-10 max-w-7xl" data-testid="admin-marketing-page">
                <div className="flex items-start justify-between mb-8">
                    <div>
                        <div className="overline">Marketing Automation</div>
                        <h1 className="font-serif text-4xl text-navy mt-2">Journey Builder</h1>
                        <p className="text-sm text-text-secondary mt-2 max-w-xl">
                            Trigger-based, multi-channel campaigns. Email · SMS · WhatsApp · Push — all events tracked and analysed.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/admin/marketing/new')}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="new-campaign-btn"
                    >
                        <Plus size={16} /> New Campaign
                    </button>
                </div>

                {/* KPI tiles */}
                {overview && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10" data-testid="marketing-kpis">
                        <KpiTile
                            icon={Megaphone} label="Active campaigns"
                            value={overview.campaigns.active}
                            sub={`${overview.campaigns.total} total · ${overview.campaigns.draft} draft`}
                            testid="kpi-active"
                        />
                        <KpiTile
                            icon={Send} label="Messages sent"
                            value={overview.sends.total.toLocaleString('en-IN')}
                            sub={`${overview.sends.last_24h} in last 24h`}
                            testid="kpi-sends"
                        />
                        <KpiTile
                            icon={TrendingUp} label="Open rate"
                            value={`${overview.engagement.open_rate}%`}
                            sub="Across all channels"
                            testid="kpi-open-rate"
                        />
                        <KpiTile
                            icon={Activity} label="Click rate"
                            value={`${overview.engagement.click_rate}%`}
                            sub="Conversions on CTAs"
                            testid="kpi-click-rate"
                        />
                    </div>
                )}

                {/* Campaign list */}
                <div className="bg-white border border-border-subtle">
                    <div className="px-6 py-4 border-b border-border-subtle flex items-center justify-between">
                        <div className="font-serif text-xl text-navy">All Campaigns</div>
                        <span className="text-xs text-text-tertiary">{campaigns.length} total</span>
                    </div>
                    {loading ? (
                        <div className="p-10 text-center text-text-secondary">Loading campaigns…</div>
                    ) : campaigns.length === 0 ? (
                        <div className="p-10 text-center text-text-secondary">No campaigns yet. Create one to begin.</div>
                    ) : (
                        <div className="divide-y divide-border-subtle" data-testid="campaign-list">
                            {campaigns.map((c) => (
                                <CampaignRow key={c.id} c={c} onChange={load} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}

function KpiTile({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="bg-white border border-border-subtle p-6" data-testid={testid}>
            <div className="flex items-start justify-between">
                <Icon size={20} strokeWidth={1.5} className="text-inorbit-blue" />
            </div>
            <div className="overline text-[10px] mt-4">{label}</div>
            <div className="font-serif text-3xl text-navy mt-1">{value}</div>
            <div className="text-xs text-text-tertiary mt-1">{sub}</div>
        </div>
    );
}

function CampaignRow({ c, onChange }) {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    const act = async (action) => {
        setBusy(true);
        try {
            await api.post(`/admin/marketing/campaigns/${c.id}/${action}`);
            toast.success(`Campaign ${action}d`);
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Action failed');
        } finally { setBusy(false); }
    };

    const send = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/marketing/campaigns/${c.id}/send-now`);
            toast.success(`Sent ${data.sent} messages to ${data.audience_size} customers`);
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Send failed');
        } finally { setBusy(false); }
    };

    const del = async () => {
        if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/marketing/campaigns/${c.id}`);
            toast.success('Campaign deleted');
            onChange?.();
        } catch { toast.error('Delete failed'); }
        finally { setBusy(false); }
    };

    return (
        <div className="px-6 py-4 flex items-center gap-6 hover:bg-ivory transition-colors" data-testid={`campaign-row-${c.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>
                        {c.status}
                    </span>
                    <Link to={`/admin/marketing/${c.id}`} className="font-medium text-navy hover:text-inorbit-orange truncate" data-testid={`campaign-name-${c.id}`}>
                        {c.name}
                    </Link>
                </div>
                <div className="text-xs text-text-secondary mt-1.5 flex items-center gap-3 flex-wrap">
                    <span>Trigger: <strong className="font-mono text-[11px]">{c.trigger}</strong></span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                        Channels: {(c.channels || []).map((ch) => {
                            const I = CHANNEL_ICONS[ch] || Bell;
                            return <I key={ch} size={12} className="text-text-tertiary" />;
                        })}
                    </span>
                    <span>·</span>
                    <span>{c.sends_count || 0} sends</span>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {c.status === 'active' ? (
                    <button onClick={() => act('pause')} disabled={busy} className="btn-outline-mini" data-testid={`pause-${c.id}`}>
                        <Pause size={12} /> Pause
                    </button>
                ) : (
                    <button onClick={() => act('activate')} disabled={busy} className="btn-outline-mini" data-testid={`activate-${c.id}`}>
                        <Play size={12} /> Activate
                    </button>
                )}
                <button onClick={send} disabled={busy} className="btn-primary-mini" data-testid={`send-now-${c.id}`}>
                    <Send size={12} /> Send Now
                </button>
                <button onClick={() => navigate(`/admin/marketing/${c.id}`)} className="text-xs text-text-secondary hover:text-inorbit-blue px-2">
                    <ChevronRight size={16} />
                </button>
                <button onClick={del} disabled={busy} className="text-red-500 hover:text-red-700 p-1" data-testid={`delete-${c.id}`}>
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}


// ============================================================
// CREATE CAMPAIGN PAGE (with template picker)
// ============================================================
export function AdminMarketingNew() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [templates, setTemplates] = useState([]);
    const [picked, setPicked] = useState(null);
    const [form, setForm] = useState({
        name: '', trigger: 'manual', channels: ['email'],
        subject: '', body: '', cta_label: 'Open Inorbit One', cta_url: '/',
        audience: { tier: 'any' }, status: 'draft', template_id: null,
    });
    const [audiencePreview, setAudiencePreview] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/admin/marketing/templates').then(({ data }) => setTemplates(data));
    }, []);

    useEffect(() => {
        const tid = params.get('template');
        if (tid && templates.length) {
            const t = templates.find((x) => x.id === tid);
            if (t) applyTemplate(t);
        }
    }, [params, templates]);

    const applyTemplate = (t) => {
        setPicked(t.id);
        setForm({
            name: t.name,
            trigger: t.trigger,
            channels: [...t.channels],
            subject: t.subject,
            body: t.body,
            cta_label: t.cta_label,
            cta_url: t.cta_url,
            audience: { ...t.audience },
            status: 'draft',
            template_id: t.id,
        });
    };

    const previewAudience = async () => {
        try {
            const { data } = await api.post('/admin/marketing/audience/preview', form.audience);
            setAudiencePreview(data);
            toast.success(`${data.total} customers match`);
        } catch { toast.error('Preview failed'); }
    };

    const save = async (activate = false) => {
        if (!form.name.trim()) { toast.error('Campaign name required'); return; }
        if (!form.subject.trim()) { toast.error('Subject required'); return; }
        if (!form.channels.length) { toast.error('Pick at least one channel'); return; }
        setSaving(true);
        try {
            const payload = { ...form, status: activate ? 'active' : 'draft' };
            const { data } = await api.post('/admin/marketing/campaigns', payload);
            toast.success(activate ? 'Campaign activated' : 'Campaign saved as draft');
            navigate(`/admin/marketing/${data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const toggleChannel = (ch) => {
        setForm((f) => ({
            ...f,
            channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch],
        }));
    };

    return (
        <AdminLayout>
            <div className="p-10 max-w-6xl" data-testid="admin-marketing-new">
                <button onClick={() => navigate('/admin/marketing')} className="text-xs text-text-secondary hover:text-inorbit-blue inline-flex items-center gap-1 mb-4">
                    <ArrowLeft size={14} /> Back to Marketing
                </button>
                <div className="overline">New Campaign</div>
                <h1 className="font-serif text-4xl text-navy mt-2 mb-8">Compose a Journey</h1>

                {/* Template picker */}
                <div className="mb-10">
                    <div className="text-xs uppercase tracking-wider text-text-secondary mb-3">Start from a template</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="template-grid">
                        {templates.map((t) => {
                            const Icon = ICONS[t.icon] || Megaphone;
                            const active = picked === t.id;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => applyTemplate(t)}
                                    className={`text-left p-4 border transition-all ${active ? 'border-inorbit-blue bg-inorbit-blue/5 shadow-sm' : 'border-border-subtle bg-white hover:border-inorbit-blue/40'}`}
                                    data-testid={`template-${t.id}`}
                                >
                                    <Icon size={18} className="text-inorbit-blue" />
                                    <div className="font-medium text-sm text-navy mt-2">{t.name}</div>
                                    <div className="text-[11px] text-text-tertiary mt-1 leading-tight">{t.tagline}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Form */}
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2 space-y-5">
                        <FormField label="Campaign Name">
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="input-box w-full"
                                placeholder="e.g. Diwali Big Bash – Tier upgrade celebration"
                                data-testid="campaign-name-input"
                            />
                        </FormField>

                        <FormField label="Subject Line">
                            <input
                                value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                className="input-box w-full"
                                placeholder="Use {first_name} for personalisation"
                                data-testid="campaign-subject-input"
                            />
                        </FormField>

                        <FormField label="Message Body">
                            <textarea
                                value={form.body}
                                onChange={(e) => setForm({ ...form, body: e.target.value })}
                                rows={6}
                                className="input-box w-full font-mono text-sm"
                                placeholder="Your message…"
                                data-testid="campaign-body-input"
                            />
                            <div className="text-[11px] text-text-tertiary mt-1.5">
                                Variables: <code className="bg-ivory px-1">{'{first_name}'}</code>{' '}
                                <code className="bg-ivory px-1">{'{mall_name}'}</code>{' '}
                                <code className="bg-ivory px-1">{'{new_tier}'}</code>
                            </div>
                        </FormField>

                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="CTA Label">
                                <input
                                    value={form.cta_label}
                                    onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                                    className="input-box w-full"
                                    data-testid="campaign-cta-label"
                                />
                            </FormField>
                            <FormField label="CTA URL">
                                <input
                                    value={form.cta_url}
                                    onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                                    className="input-box w-full"
                                    data-testid="campaign-cta-url"
                                />
                            </FormField>
                        </div>
                    </div>

                    {/* Sidebar config */}
                    <div className="space-y-5">
                        <FormField label="Trigger">
                            <select
                                value={form.trigger}
                                onChange={(e) => setForm({ ...form, trigger: e.target.value })}
                                className="input-box w-full"
                                data-testid="campaign-trigger"
                            >
                                <option value="manual">Manual send</option>
                                <option value="signup">New signup</option>
                                <option value="cart_abandoned">Cart abandoned</option>
                                <option value="birthday">Birthday</option>
                                <option value="tier_upgraded">Tier upgraded</option>
                                <option value="inactive_30d">Inactive 30d</option>
                                <option value="mall_pass_expiring">Mall Pass expiring</option>
                            </select>
                        </FormField>

                        <FormField label="Channels">
                            <div className="grid grid-cols-2 gap-2" data-testid="channel-picker">
                                {['email', 'sms', 'whatsapp', 'push'].map((ch) => {
                                    const Icon = CHANNEL_ICONS[ch];
                                    const active = form.channels.includes(ch);
                                    return (
                                        <button
                                            key={ch}
                                            type="button"
                                            onClick={() => toggleChannel(ch)}
                                            className={`flex items-center gap-2 px-3 py-2 border text-xs uppercase tracking-wider ${active ? 'bg-inorbit-blue text-white border-inorbit-blue' : 'border-border-subtle text-text-secondary hover:border-inorbit-blue'}`}
                                            data-testid={`channel-${ch}`}
                                        >
                                            <Icon size={12} /> {ch}
                                        </button>
                                    );
                                })}
                            </div>
                        </FormField>

                        <FormField label="Audience – Tier">
                            <select
                                value={form.audience.tier || 'any'}
                                onChange={(e) => setForm({ ...form, audience: { ...form.audience, tier: e.target.value } })}
                                className="input-box w-full"
                                data-testid="audience-tier"
                            >
                                <option value="any">Any tier</option>
                                <option value="silver">Silver</option>
                                <option value="gold">Gold</option>
                                <option value="platinum">Platinum</option>
                                <option value="black">Black</option>
                            </select>
                        </FormField>

                        <FormField label="Min Inactive Days (optional)">
                            <input
                                type="number"
                                value={form.audience.min_inactive_days || ''}
                                onChange={(e) => setForm({
                                    ...form,
                                    audience: { ...form.audience, min_inactive_days: e.target.value ? Number(e.target.value) : null },
                                })}
                                className="input-box w-full"
                                placeholder="e.g. 30"
                                data-testid="audience-inactive-days"
                            />
                        </FormField>

                        <button onClick={previewAudience} className="btn-outline w-full justify-center text-xs" data-testid="preview-audience-btn">
                            <Users size={14} /> Preview Audience
                        </button>
                        {audiencePreview && (
                            <div className="bg-ivory p-3 text-xs" data-testid="audience-preview-result">
                                <div className="font-medium text-navy">{audiencePreview.total} customers match</div>
                                <div className="text-text-tertiary mt-1">Sample: {audiencePreview.sample.slice(0, 3).map((s) => s.name).join(', ')}{audiencePreview.sample.length > 3 ? '…' : ''}</div>
                            </div>
                        )}

                        <div className="space-y-2 pt-2 border-t border-border-subtle">
                            <button onClick={() => save(false)} disabled={saving} className="btn-outline w-full justify-center" data-testid="save-draft-btn">
                                {saving ? 'Saving…' : 'Save as Draft'}
                            </button>
                            <button onClick={() => save(true)} disabled={saving} className="btn-primary w-full justify-center" data-testid="save-activate-btn">
                                {saving ? 'Activating…' : 'Save & Activate'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

function FormField({ label, children }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider text-text-secondary block mb-1.5">{label}</label>
            {children}
        </div>
    );
}


// ============================================================
// CAMPAIGN DETAIL + ANALYTICS
// ============================================================
export function AdminMarketingDetail() {
    const { cid } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [sends, setSends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [c, a, s] = await Promise.all([
                api.get(`/admin/marketing/campaigns/${cid}`),
                api.get(`/admin/marketing/campaigns/${cid}/analytics`),
                api.get(`/admin/marketing/campaigns/${cid}/sends?limit=30`),
            ]);
            setCampaign(c.data);
            setAnalytics(a.data);
            setSends(s.data);
        } catch { toast.error('Could not load campaign'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [cid]);

    const sendNow = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/marketing/campaigns/${cid}/send-now`);
            toast.success(`Sent ${data.sent} messages to ${data.audience_size} customers`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
        finally { setBusy(false); }
    };

    const act = async (action) => {
        setBusy(true);
        try {
            await api.post(`/admin/marketing/campaigns/${cid}/${action}`);
            toast.success(`Campaign ${action}d`);
            await load();
        } catch { toast.error('Failed'); }
        finally { setBusy(false); }
    };

    if (loading) return <AdminLayout><div className="p-10">Loading…</div></AdminLayout>;
    if (!campaign) return <AdminLayout><div className="p-10">Campaign not found</div></AdminLayout>;

    return (
        <AdminLayout>
            <div className="p-10 max-w-7xl" data-testid="admin-marketing-detail">
                <button onClick={() => navigate('/admin/marketing')} className="text-xs text-text-secondary hover:text-inorbit-blue inline-flex items-center gap-1 mb-4">
                    <ArrowLeft size={14} /> Back to Marketing
                </button>

                <div className="flex items-start justify-between mb-8">
                    <div>
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${STATUS_COLORS[campaign.status] || STATUS_COLORS.draft}`}>
                            {campaign.status}
                        </span>
                        <h1 className="font-serif text-4xl text-navy mt-3" data-testid="campaign-name">{campaign.name}</h1>
                        <div className="text-sm text-text-secondary mt-2">
                            Trigger: <strong className="font-mono text-xs bg-ivory px-1.5 py-0.5">{campaign.trigger}</strong>
                            <span className="mx-2">·</span>
                            Channels: {(campaign.channels || []).join(', ')}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {campaign.status === 'active' ? (
                            <button onClick={() => act('pause')} disabled={busy} className="btn-outline inline-flex items-center gap-2" data-testid="detail-pause-btn">
                                <Pause size={14} /> Pause
                            </button>
                        ) : (
                            <button onClick={() => act('activate')} disabled={busy} className="btn-outline inline-flex items-center gap-2" data-testid="detail-activate-btn">
                                <Play size={14} /> Activate
                            </button>
                        )}
                        <button onClick={sendNow} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="detail-send-now-btn">
                            <Send size={14} /> Send Now
                        </button>
                    </div>
                </div>

                {/* Analytics */}
                {analytics && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="campaign-analytics">
                        <KpiTile icon={Inbox} label="Total sends" value={analytics.total} sub="" testid="ana-total" />
                        <KpiTile icon={Send} label="Delivered" value={`${analytics.delivery_rate}%`} sub={`${analytics.delivered} msg`} testid="ana-delivered" />
                        <KpiTile icon={TrendingUp} label="Open rate" value={`${analytics.open_rate}%`} sub={`${analytics.opened} opens`} testid="ana-open" />
                        <KpiTile icon={Activity} label="Click rate" value={`${analytics.click_rate}%`} sub={`${analytics.clicked} clicks`} testid="ana-click" />
                        <KpiTile icon={BarChart3} label="Failed" value={analytics.failed} sub="Bounce / err" testid="ana-failed" />
                    </div>
                )}

                {/* Preview + Recent Sends */}
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white border border-border-subtle p-6">
                        <div className="overline mb-3">Message Preview</div>
                        <div className="border border-border-subtle p-4 bg-ivory">
                            <div className="text-xs text-text-tertiary mb-1">Subject</div>
                            <div className="font-medium text-navy mb-3">{campaign.subject}</div>
                            <div className="text-xs text-text-tertiary mb-1">Body</div>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-text-secondary leading-relaxed">{campaign.body}</pre>
                            {campaign.cta_label && (
                                <a href={campaign.cta_url} className="inline-block mt-4 btn-primary text-xs">
                                    {campaign.cta_label}
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="bg-white border border-border-subtle">
                        <div className="px-6 py-4 border-b border-border-subtle font-serif text-lg text-navy">Recent Sends</div>
                        <div className="max-h-[500px] overflow-y-auto divide-y divide-border-subtle" data-testid="sends-list">
                            {sends.length === 0 ? (
                                <div className="p-6 text-center text-text-secondary text-sm">No sends yet — click &ldquo;Send Now&rdquo;</div>
                            ) : sends.map((s) => (
                                <div key={s.id} className="px-6 py-3 text-xs flex items-center gap-3" data-testid={`send-${s.id}`}>
                                    <span className={`px-1.5 py-0.5 uppercase tracking-wider ${s.status === 'failed' || s.status === 'bounced' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                        {s.status}
                                    </span>
                                    <span className="font-mono text-[10px] text-text-tertiary uppercase">{s.channel}</span>
                                    <span className="flex-1 truncate text-text-secondary">{s.customer_email}</span>
                                    <span className="text-text-tertiary">{s.opened ? '👁' : ''} {s.clicked ? '🔗' : ''}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
